import {
  Component,
  Input,
  inject,
  ChangeDetectionStrategy,
} from '@angular/core';
import { map, take, tap } from 'rxjs';
import { selectActiveWorkspaceId } from 'src/app/core/reducers';
import { UserWorkspacesService } from 'src/app/core/services/user-workspaces.service';

import { AppState } from 'src/app/reducers';
import { DataObject } from 'src/app/core/models/core.models';
import { FormProperty } from 'src/app/core/models/form.models';
import { FormsService } from 'src/app/core/services/forms.service';
import { GeneralDynamicFormComponent } from '../../../general-dynamic-form/general-dynamic-form.component';
import { LoggerService } from '@services/logger.service';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { Validators } from '@angular/forms';

@Component({
  selector: 'app-dashboard-duplicate-form',
  templateUrl: './dashboard-duplicate-form.component.html',
  styleUrls: ['./dashboard-duplicate-form.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class DashboardDuplicateFormComponent extends GeneralDynamicFormComponent {
  private userWorkspaces = inject(UserWorkspacesService);

  @Input() objects: DataObject[] = [];

  /**
   * Optional: if you have the same "dimension selection" constraint as feeds
   * you can wire it the same way. Otherwise remove it and the related shouldDisplay().
   */
  @Input() hasDimensionInSelection = false;

  public activeFormProperties: FormProperty[] = [];
  public formProperties: FormProperty[] = [];

  constructor(
    protected store: Store<AppState>,
    protected formService: FormsService,
    protected translateService: TranslateService,
    protected logger: LoggerService,
  ) {
    super(formService, store, logger);
  }

  ngOnInit() {
    const dashboardOptions = this.formService.duplicateDashboardOptions;

    this.formProperties = [
      {
        propertyName: 'workspace_id',
        validators: [Validators.required],
        propertyLabel: this.translateService.instant(
          'dashboard-duplicate-form.label.target-workspace',
        ),
        controlType: 'select',
        valueOptionsAsync: this.userWorkspaces.sortedWorkspaces$().pipe(
          map((workspaces) =>
            workspaces.map((workspace) => ({
              value: workspace.id,
              name: workspace.title,
            })),
          ),
        ),
      },
      {
        propertyName: 'options',
        controlType: 'checkbox-array',
        propertyGroup: {
          key: 'dashboard-duplicate-form.group.copy-options',
          style: 'copy-options',
        },
        additionalConfig: {
          values: dashboardOptions,
        },
      },
    ];

    if (this.objects.length === 1) {
      this.formProperties.unshift(
        {
          propertyName: 'title',
          propertyLabel: this.translateService.instant(
            'dashboard-duplicate-form.label.title',
          ),
          controlType: 'text',
          additionalConfig: {
            placeholder: this.translateService.instant(
              'dashboard-duplicate-form.placeholder.title',
            ),
          },
          validators: [Validators.required],
        },
        {
          propertyName: 'description',
          propertyLabel: this.translateService.instant(
            'dashboard-duplicate-form.label.description',
          ),
          controlType: 'textarea',
          additionalConfig: {
            placeholder: this.translateService.instant(
              'dashboard-duplicate-form.placeholder.description',
            ),
            rows: 4,
          },
        },
      );
    } else {
      this.formProperties.unshift({
        propertyName: 'prefix',
        propertyLabel: this.translateService.instant(
          'dashboard-duplicate-form.label.name-prefix',
        ),
        controlType: 'text',
        additionalConfig: {
          placeholder: this.translateService.instant(
            'dashboard-duplicate-form.placeholder.name-prefix',
          ),
        },
        validators: [Validators.required],
      });
    }

    super.ngOnInit();

    // Default workspace selection = active workspace
    this.store
      .select(selectActiveWorkspaceId)
      .pipe(
        take(1),
        tap((workspaceId) => {
          this.formService.populateFormValues(this.formGroup, {
            workspace_id: workspaceId,
          });
        }),
      )
      .subscribe();

    // Default values based on selection size
    if (this.objects.length === 1) {
      this.formService.populateFormValues(this.formGroup, {
        title: 'Copy of ' + (this.objects[0]?.title ?? ''),
        description: this.objects[0]?.description ?? '',
        workspace_id:
          this.objects[0]?.workspace_id ??
          this.formGroup.get('workspace_id')?.value,
      });
    } else {
      this.formService.populateFormValues(this.formGroup, {
        prefix: 'Copy of ',
      });
    }

    this.formService.populateFormValues(this.formGroup, {
      options: ['duplicateExportSchedules', 'duplicatePermissions'],
    });
  }
}
